import { error } from '@sonar-software/xuder';
import { getFilenameFromContentDisposition } from '@sonar-software/sonar-ts-lib';
import { useAction } from './useAction';
import { useAppNotifications } from './app';
import { useCallback } from 'react';
import { ApiPromise, HttpResponse } from '@sonar-software/sonar-http';

function downloadFile(response: HttpResponse<Blob>): void {
	if (response.data) {
		const blobUrl = URL.createObjectURL(response.data);
		const contentDisposition = response.headers.get('content-disposition');
		const filename = getFilenameFromContentDisposition(contentDisposition ?? '');
		const tempLink = document.createElement('a');

		tempLink.style.display = 'none';
		tempLink.href = blobUrl;
		tempLink.setAttribute('download', filename);
		document.body.appendChild(tempLink);
		tempLink.click();
		document.body.removeChild(tempLink);

		setTimeout(() => {
			// For Firefox it is necessary to delay revoking the ObjectURL
			URL.revokeObjectURL(blobUrl);
		}, 100);
	}
}

export type UseDownloadOptions = {
	errorMessage?: string;
	onFinish?: () => void;
	request: () => ApiPromise<Blob>;
};

export function useDownload(): (options: UseDownloadOptions) => void {
	const logError = useAction(error);
	const { errorNotification } = useAppNotifications();

	const download = useCallback(async(options: UseDownloadOptions) => {
		const { errorMessage, onFinish, request } = options;

		try {
			const httpResponse = await request();

			if (httpResponse.data) {
				downloadFile(httpResponse);
			} else {
				throw new Error('Unable to download file.');
			}
		} catch (ex) {
			logError(ex);
			errorNotification(errorMessage);
		} finally {
			onFinish?.();
		}
	}, [ errorNotification, logError ]);

	return download;
}
